import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Invoice } from "@/types/stock";
import {
  FileText,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  Download,
  Send,
  Edit,
  CreditCard,
  Clock,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";

interface InvoiceViewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onEdit?: (invoice: Invoice) => void;
  onDownloadPDF?: (invoice: Invoice) => void;
  onSendEmail?: (invoice: Invoice) => void;
  onRecordPayment?: (invoice: Invoice) => void;
}

const InvoiceViewDialog: React.FC<InvoiceViewDialogProps> = ({
  isOpen,
  onClose,
  invoice,
  onEdit,
  onDownloadPDF,
  onSendEmail,
  onRecordPayment,
}) => {
  const { t } = useTranslation();

  if (!invoice) return null;

  const getStatusBadge = (status: Invoice["status"]) => {
    const statusConfig = {
      draft: {
        variant: "secondary" as const,
        label: t("invoices.status.draft"),
        icon: <Edit className="h-3 w-3" />,
      },
      sent: {
        variant: "default" as const,
        label: t("invoices.status.sent"),
        icon: <Send className="h-3 w-3" />,
      },
      paid: {
        variant: "default" as const,
        label: t("invoices.status.paid"),
        className: "bg-green-500 hover:bg-green-600",
        icon: <CheckCircle className="h-3 w-3" />,
      },
      overdue: {
        variant: "destructive" as const,
        label: t("invoices.status.overdue"),
        icon: <AlertTriangle className="h-3 w-3" />,
      },
      cancelled: {
        variant: "outline" as const,
        label: t("invoices.status.cancelled"),
        icon: <Clock className="h-3 w-3" />,
      },
    };

    const config = statusConfig[status];
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.icon}
        <span className="ml-1">{config.label}</span>
      </Badge>
    );
  };

  const getPaymentStatusBadge = (status: string) => {
    const paymentConfig = {
      unpaid: {
        variant: "destructive" as const,
        label: t("invoices.paymentStatus.unpaid"),
        icon: <CreditCard className="h-3 w-3" />,
      },
      partial: {
        variant: "default" as const,
        label: t("invoices.paymentStatus.partial"),
        className: "bg-yellow-500 hover:bg-yellow-600",
        icon: <CreditCard className="h-3 w-3" />,
      },
      paid: {
        variant: "default" as const,
        label: t("invoices.paymentStatus.paid"),
        className: "bg-green-500 hover:bg-green-600",
        icon: <CheckCircle className="h-3 w-3" />,
      },
      overpaid: {
        variant: "default" as const,
        label: t("invoices.paymentStatus.overpaid"),
        className: "bg-blue-500 hover:bg-blue-600",
        icon: <CreditCard className="h-3 w-3" />,
      },
    };

    const config = paymentConfig[status as keyof typeof paymentConfig];
    if (!config) return null;

    return (
      <Badge variant={config.variant} className={config.className}>
        {config.icon}
        <span className="ml-1">{config.label}</span>
      </Badge>
    );
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(3)} TND`;
  };

  const calculateItemTotal = (item: any) => {
    const subtotal = item.quantity * item.unitPrice;
    const discount = (subtotal * item.discountPercentage) / 100;
    return subtotal - discount;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t("invoices.viewInvoice")} - {invoice.invoiceNumber}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6">
            {/* Invoice Header */}
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold">{invoice.invoiceNumber}</h2>
                <p className="text-muted-foreground">
                  {t("invoices.createdOn")} {formatDate(invoice.createdAt)}
                </p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                {getStatusBadge(invoice.status)}
                {/* Enhanced payment status - will be available after database migration */}
                {(invoice as any).paymentStatus &&
                  getPaymentStatusBadge((invoice as any).paymentStatus)}
              </div>
            </div>

            <Separator />

            {/* Client Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t("invoices.clientInformation")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{invoice.clientName}</span>
                  </div>
                  {invoice.clientPhone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{invoice.clientPhone}</span>
                    </div>
                  )}
                  {invoice.clientEmail && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{invoice.clientEmail}</span>
                    </div>
                  )}
                  {invoice.clientAddress && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{invoice.clientAddress}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Invoice Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {t("invoices.invoiceDetails")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("invoices.invoiceDate")}:
                    </span>
                    <span>{formatDate(invoice.createdAt)}</span>
                  </div>
                  {invoice.dueDate && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {t("invoices.dueDate")}:
                      </span>
                      <span>{formatDate(invoice.dueDate)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("invoices.taxRate")}:
                    </span>
                    <span>{invoice.taxRate}%</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    {t("invoices.paymentSummary")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("invoices.subtotal")}:
                    </span>
                    <span>{formatCurrency(invoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("invoices.tax")}:
                    </span>
                    <span>{formatCurrency(invoice.taxAmount)}</span>
                  </div>
                  {invoice.discountAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {t("invoices.discount")}:
                      </span>
                      <span>-{formatCurrency(invoice.discountAmount)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-bold text-lg">
                    <span>{t("invoices.total")}:</span>
                    <span>{formatCurrency(invoice.totalAmount)}</span>
                  </div>

                  {/* Enhanced payment tracking - will be available after database migration */}
                  {(invoice as any).paidAmount !== undefined && (
                    <>
                      <Separator />
                      <div className="flex justify-between text-green-600">
                        <span>{t("invoices.paidAmount")}:</span>
                        <span>
                          {formatCurrency((invoice as any).paidAmount || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>{t("invoices.remainingAmount")}:</span>
                        <span>
                          {formatCurrency(
                            (invoice as any).remainingAmount ||
                              invoice.totalAmount
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Invoice Items */}
            <Card>
              <CardHeader>
                <CardTitle>{t("invoices.items")}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("invoices.item")}</TableHead>
                      <TableHead className="text-right">
                        {t("invoices.quantity")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("invoices.unitPrice")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("invoices.discount")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("invoices.total")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {item.productName}
                            </div>
                            {item.description && (
                              <div className="text-sm text-muted-foreground">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.quantity}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.unitPrice)}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.discountPercentage > 0
                            ? `${item.discountPercentage}%`
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(calculateItemTotal(item))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Notes and Terms */}
            {(invoice.notes || invoice.termsAndConditions) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {invoice.notes && (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t("invoices.notes")}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm whitespace-pre-wrap">
                        {invoice.notes}
                      </p>
                    </CardContent>
                  </Card>
                )}
                {invoice.termsAndConditions && (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t("invoices.termsAndConditions")}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm whitespace-pre-wrap">
                        {invoice.termsAndConditions}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2 flex-wrap">
            {onEdit && (
              <Button variant="outline" onClick={() => onEdit(invoice)}>
                <Edit className="h-4 w-4 mr-2" />
                {t("common.edit")}
              </Button>
            )}
            {onDownloadPDF && (
              <Button variant="outline" onClick={() => onDownloadPDF(invoice)}>
                <Download className="h-4 w-4 mr-2" />
                {t("invoices.downloadPDF")}
              </Button>
            )}
            {onSendEmail && (
              <Button variant="outline" onClick={() => onSendEmail(invoice)}>
                <Send className="h-4 w-4 mr-2" />
                {t("invoices.sendEmail")}
              </Button>
            )}
            {onRecordPayment && invoice.status !== "paid" && (
              <Button
                variant="default"
                onClick={() => onRecordPayment(invoice)}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {t("invoices.recordPayment")}
              </Button>
            )}
          </div>
          <Button variant="secondary" onClick={onClose}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceViewDialog;
